// Info tab under Settings → Users. Spells out exactly what every button does, because the
// difference between pausing someone and removing them is easy to get wrong and hard to undo
// by guesswork. Read-only reference — nothing here changes anything.
import React from 'react';

const STATES: { name: string; colour: string; inList: string; login: string; employment: string; use: string }[] = [
  {
    name: 'Active',
    colour: 'bg-green-900/50 text-green-400',
    inList: 'Yes — in Users and in Workforce',
    login: 'Can log in',
    employment: 'Employed, accruing expected hours',
    use: 'Anyone currently working here.',
  },
  {
    name: 'Paused',
    colour: 'bg-amber-900/50 text-amber-400',
    inList: 'Yes — stays in both lists',
    login: 'Cannot log in',
    employment: 'Still employed — period stays open',
    use: 'Someone who works a few days a month. Cut their access between stints, switch it back on when they return.',
  },
  {
    name: 'Gone',
    colour: 'bg-gray-700 text-gray-400',
    inList: 'No — hidden from both lists',
    login: 'Cannot log in',
    employment: 'Employment period closed',
    use: 'They have left. Keeps the lists short without losing anything they did.',
  },
];

const BUTTONS: { where: string; label: string; what: string; note?: string }[] = [
  {
    where: 'Users',
    label: 'Add user',
    what: 'Creates a login. It does NOT make them a tracked worker — to get expected hours and appear in the calendar, give them an employment period under Work time → Workforce.',
  },
  {
    where: 'Users',
    label: 'Edit',
    what: 'Changes name, email and (admins only) role. Never touches history.',
  },
  {
    where: 'Users',
    label: 'Reset password',
    what: 'Sets a new password directly. The person is not notified — tell them yourself.',
  },
  {
    where: 'Users',
    label: 'Deactivate',
    what: 'PAUSES the account. They cannot log in, any open session ends within a minute, and they stay in both lists so you can switch them back on.',
    note: 'They remain employed and keep accruing expected hours. Use this for irregular workers between stints — not for someone who has left.',
  },
  {
    where: 'Users',
    label: 'Reactivate',
    what: 'Restores access to a paused account. Sessions from before the pause stay dead, so they sign in fresh.',
  },
  {
    where: 'Users',
    label: 'Delete',
    what: 'Removes the account from the Users list and the Workforce table, ends the login, and closes their employment period. It only affects things going forward.',
    note: 'Nothing they did is deleted. Orders, work calendar, printed labels, returns and P&P speed all keep their name. They still appear in reports for the months they actually worked — so headcount and speed figures for past periods stay correct.',
  },
  {
    where: 'Work time → Workforce',
    label: 'End',
    what: 'The same outcome as Delete, with the HR detail: last day, reason, and the leaver checklist. Use this when someone leaves properly.',
  },
  {
    where: 'Work time → Workforce',
    label: 'Rehire a former worker',
    what: 'Brings a past worker back on their original account: opens a new employment period and restores the login. Their earlier periods are untouched, so the gap in service stays visible.',
  },
  {
    where: 'Work time → Workforce',
    label: 'Terms → Terms changed',
    what: 'For a real change (temp to permanent, different days). Closes the current period and opens a new one, so both sets of terms stay in the history.',
  },
  {
    where: 'Work time → Workforce',
    label: 'Terms → Fix a mistake',
    what: 'For when the record is simply wrong. Corrects the period in place and adds nothing to the timeline.',
  },
];

export default function UsersInfo() {
  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h3 className="text-white font-semibold">What each button does</h3>
        <p className="text-gray-500 text-sm mt-1">
          The Users list is where active workers are managed. Two things can take someone out of
          circulation and they are not the same — pausing is temporary, deleting is permanent.
          Neither one ever removes history.
        </p>
      </div>

      {/* The three states */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">
          The three account states
        </div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-left font-medium">Shown in lists?</th>
                <th className="px-3 py-2 text-left font-medium">Login</th>
                <th className="px-3 py-2 text-left font-medium">Employment</th>
                <th className="px-3 py-2 text-left font-medium">When to use it</th>
              </tr>
            </thead>
            <tbody>
              {STATES.map(s => (
                <tr key={s.name} className="border-b border-gray-800/50 align-top">
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.colour}`}>{s.name}</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-400 text-xs">{s.inList}</td>
                  <td className="px-3 py-2.5 text-gray-400 text-xs">{s.login}</td>
                  <td className="px-3 py-2.5 text-gray-400 text-xs">{s.employment}</td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">{s.use}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* What history survives */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">
          What happens to their work
        </div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-4 py-3.5 space-y-2.5">
          <p className="text-sm text-gray-300">
            No button on this page deletes work history. Ever.
          </p>
          <ul className="text-xs text-gray-500 space-y-1.5 list-disc pl-4">
            <li>Who picked, packed and printed every order — kept, with their name.</li>
            <li>Work calendar days, hours and absences for the months they worked — kept.</li>
            <li>P&amp;P speed, per-user statistics, returns and stock counts — kept.</li>
            <li>
              Monthly reports for past periods still include them, so
              <span className="text-gray-300"> how many people worked in a given month, and who they
              were, stays correct</span> after they leave.
            </li>
            <li>
              Their username stays reserved permanently. A future hire can never be given the same
              username and inherit their record.
            </li>
          </ul>
          <p className="text-[11px] text-gray-600 pt-1 border-t border-gray-800/60">
            The only exception: deleting an account that has never done anything at all — a typo or a
            mistaken invite — removes it outright, because there is nothing to preserve.
          </p>
        </div>
      </div>

      {/* Button by button */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">
          Button by button
        </div>
        <div className="space-y-2">
          {BUTTONS.map((b, i) => (
            <div key={i} className="bg-[#1a1d27] rounded-xl border border-gray-800 px-4 py-3">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-gray-200 font-medium text-sm">{b.label}</span>
                <span className="text-[10px] uppercase tracking-wide text-gray-600">{b.where}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{b.what}</p>
              {b.note && (
                <p className="text-xs text-gray-400 mt-1.5 pl-3 border-l-2 border-gray-700">{b.note}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-gray-600 border-t border-gray-800 pt-3">
        Behaving differently than described here? That's a bug — please report it.
      </p>
    </div>
  );
}
